import { useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number;
  rootMargin?: string;
}

/**
 * Custom hook for implementing infinite scroll
 *
 * @return {React.RefObject<HTMLDivElement>} Sentinel element ref
 *
 * @example
 * ```tsx
 * const sentinelRef = useInfiniteScroll({
 *   onLoadMore: loadMore,
 *   hasMore,
 *   isLoading: loading,
 * });
 *
 * // In JSX:
 * <Box ref={sentinelRef} sx={{ height: "1px" }} />
 * ```
 */
export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 0.1,
  rootMargin = "0px",
}: UseInfiniteScrollOptions) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);

  // Keep the latest onLoadMore function in ref without causing re-renders
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMoreRef.current();
        }
      },
      { threshold, rootMargin },
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, threshold, rootMargin]);

  return sentinelRef;
};
